import { useEffect, useRef } from 'react'
import { preload } from 'react-dom'
import { type MetaFunction, Link, redirect } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { type Route } from './+types/index.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	if (userId) throw redirect('/dashboard')
	return null
}

export const meta: MetaFunction = () => [
	{ title: 'xcelera | Practical website performance monitoring' },
	{
		name: 'description',
		content:
			'xcelera runs continuous Lighthouse audits from 30+ regions, tracks performance over time, and integrates with your CI/CD pipeline.',
	},
]

const features = [
	{
		name: 'scheduled_audits',
		icon: 'clock' as const,
		description:
			'Schedule lighthouse audits at regular intervals, on every deploy, or on demand.',
	},
	{
		name: 'lighthouse_reports',
		icon: 'file-text' as const,
		description:
			'Industry-standard Lighthouse reports for every audit, with actionable insights.',
	},
	{
		name: 'network_shaping',
		icon: 'laptop' as const,
		description:
			'Packet-level throttling at the OS layer applies real mobile network conditions to every run.',
	},
	{
		name: 'trend_analysis',
		icon: 'bar-chart' as const,
		description:
			'Track Core Web Vitals across deploys, not just in one-off reports.',
	},
	{
		name: 'global_regions',
		icon: 'globe' as const,
		description:
			'Run audits from 18 distinct regions around the globe. Test from where your users are based.',
	},
	{
		name: 'ci_integration',
		icon: 'github-logo' as const,
		description:
			'View performance data in your CI/CD pipeline. Catch regressions before they reach production.',
	},
	{
		name: 'api_webhooks',
		icon: 'webhook' as const,
		description:
			'Programmatic access to trigger audits and get webhook notifications. Build performance into any workflow.',
	},
]

export default function Index() {
	// The hero <picture> sits at the tail of the (CSS-inlined) document, so the
	// preload scanner can't discover it until the last chunk arrives. Preload it
	// from <head> so the LCP image starts downloading with the first bytes.
	preload('/img/dashboard-1024.avif', {
		as: 'image',
		type: 'image/avif',
		fetchPriority: 'high',
		imageSrcSet:
			'/img/dashboard-768.avif 768w, /img/dashboard-1024.avif 1024w, /img/dashboard-1920.avif 1920w',
		imageSizes: '(min-width: 1088px) 1024px, 100vw',
	})
	return (
		<main className="flex min-h-full flex-col">
			{/* Hero */}
			<section className="relative overflow-hidden px-4 pt-16 pb-0 sm:px-6 sm:pt-24 lg:px-8">
				<div className="mx-auto max-w-5xl">
					<h1 className="animate-fade-up text-foreground font-mono text-3xl font-medium tracking-tighter sm:text-5xl md:text-6xl">
						Move fast.
						<br />
						Don't break performance.
					</h1>
					<p className="animate-fade-up border-primary text-muted-foreground mt-6 max-w-xl border-l-2 pl-4 text-lg leading-relaxed [animation-delay:100ms]">
						Continuous Lighthouse auditing, on every deploy.
						<br />
						Visualize trends. Catch regressions early.
					</p>
					<div className="animate-fade-up mt-8 flex items-center gap-4 [animation-delay:200ms]">
						<Link
							to="/signup"
							className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:outline-primary rounded-md px-5 py-2.5 font-mono text-sm font-medium tracking-wide transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							Start free
						</Link>
						<Link
							to="/pricing"
							className="text-muted-foreground hover:text-foreground font-mono text-xs underline underline-offset-4 transition-colors"
						>
							50 audits/mo free
						</Link>
					</div>
				</div>

				<div className="animate-rise-up relative mx-auto mt-16 max-w-5xl [animation-delay:350ms]">
					<div className="from-background pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40 bg-gradient-to-t to-transparent" />
					<picture>
						<source
							type="image/avif"
							srcSet="/img/dashboard-768.avif 768w, /img/dashboard-1024.avif 1024w, /img/dashboard-1920.avif 1920w"
							sizes="(min-width: 1088px) 1024px, 100vw"
						/>
						<img
							src="/img/dashboard.png"
							alt="xcelera dashboard showing Lighthouse performance scores and trends"
							width={1920}
							height={1080}
							fetchPriority="high"
							className="relative rounded-t-xl border border-b-0 shadow-2xl"
						/>
					</picture>
				</div>
			</section>

			{/* Why Performance Matters */}
			<section className="px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
				<div className="mx-auto max-w-5xl">
					<ScrollReveal>
						<span className="text-muted-foreground font-mono text-xs tracking-widest uppercase">
							// why performance matters
						</span>
						<h2 className="text-foreground mt-4 max-w-2xl font-mono text-2xl font-medium tracking-tight sm:text-3xl">
							More code is being shipped than ever.
						</h2>
						<p className="text-muted-foreground mt-4 max-w-2xl text-base leading-relaxed">
							Larger bundles, more third-party scripts, more dynamic content.
							Todays traffic is predominantly mobile, often on power-constrained
							devices with intermittent connectivity.
						</p>
						<p className="text-muted-foreground mt-2">
							The impact is measurable:
						</p>
					</ScrollReveal>

					<div className="mt-8 max-w-2xl space-y-4">
						<ScrollReveal delay={100}>
							<p className="border-primary border-l-2 pl-4 text-base leading-relaxed">
								<span className="text-primary font-mono text-xl font-semibold tracking-tighter">
									7% conversion drop
								</span>
								<span className="text-muted-foreground ml-2">
									per second of added load time.
								</span>
							</p>
						</ScrollReveal>
						<ScrollReveal delay={200}>
							<p className="border-primary border-l-2 pl-4 text-base leading-relaxed">
								<span className="text-primary font-mono text-xl font-semibold tracking-tighter">
									100ms
								</span>
								<span className="text-muted-foreground ml-2">
									is all it takes for users to notice, and rankings to suffer.
								</span>
							</p>
						</ScrollReveal>
					</div>
				</div>
			</section>

			{/* What xcelera Does + Features */}
			<section className="px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-5xl">
					<ScrollReveal>
						<span className="text-muted-foreground font-mono text-xs tracking-widest uppercase">
							// what xcelera does
						</span>
						<h2 className="text-foreground mt-4 max-w-2xl font-mono text-2xl font-medium tracking-tight sm:text-3xl">
							Lighthouse-as-a-Service.
						</h2>
						<p className="text-muted-foreground mt-4 max-w-2xl text-base leading-relaxed">
							Everything you need to stay on top of web performance:
						</p>
					</ScrollReveal>

					<div className="mt-8">
						{features.map((feature, i) => (
							<ScrollReveal key={feature.name} delay={i * 60}>
								<div className="border-border grid gap-2 border-t py-6 md:grid-cols-[200px_1fr] md:gap-4">
									<h3 className="text-primary flex items-center gap-4 font-mono text-sm font-medium tracking-wide">
										<span className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
											<Icon
												name={feature.icon}
												size="sm"
												className="text-muted-foreground"
											/>
										</span>
										{feature.name}
									</h3>
									<p className="text-foreground text-base leading-relaxed">
										{feature.description}
									</p>
								</div>
							</ScrollReveal>
						))}
					</div>
				</div>
			</section>

			{/* Closer */}
			<section className="border-t px-4 py-20 sm:px-6 sm:py-32 lg:px-8">
				<div className="mx-auto max-w-5xl text-center">
					<ScrollReveal>
						<h2 className="text-foreground mt-4 font-mono text-2xl font-medium tracking-tight sm:text-3xl">
							Run your first audit in about two minutes.
						</h2>
						<p className="text-muted-foreground py-3 font-mono text-sm tracking-wide">
							Built for indie developers, freelancers, and small teams.
						</p>
						<div className="mt-6 flex items-center justify-center gap-4">
							<Link
								to="/signup"
								className="bg-foreground text-background hover:bg-foreground/90 rounded-md px-5 py-2.5 font-mono text-sm font-medium tracking-wide shadow-sm transition-colors"
							>
								Start free
							</Link>
							<Link
								to="/pricing"
								className="text-muted-foreground hover:text-foreground font-mono text-sm underline underline-offset-4 transition-colors"
							>
								See pricing
							</Link>
						</div>
					</ScrollReveal>
				</div>
			</section>
		</main>
	)
}

function ScrollReveal({
	children,
	delay = 0,
}: {
	children: React.ReactNode
	delay?: number
}) {
	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const el = ref.current
		if (!el) return

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting) {
					el.style.animationDelay = `${delay}ms`
					el.classList.add('animate-fade-up')
					observer.unobserve(el)
				}
			},
			{ threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
		)

		observer.observe(el)
		return () => observer.disconnect()
	}, [delay])

	return (
		<div ref={ref} className="opacity-0">
			{children}
		</div>
	)
}
